import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { themeColor } from 'utils/styles'
import { connect } from 'react-redux'
import strings from 'utils/strings'
import { Header, Paragraph, HoverableInfoIcon } from 'components/patterns'
import { getPowerNetLevelSimulationValues } from 'selectors/feature/simulation'
import { POWER_NET_LEVEL_SIGNAL_URIS } from 'constants/featureOutputs'
import { updateSimulatedValue } from 'actions/feature/simulation'
import ConditionSimulator from 'components/feature/Simulation/ConditionSimulator/ConditionSimulator'
import PowerNetLevelTable from 'components/feature/NavigatorPanes/OutputNodePane/OutputNodeEditor/PowerNetLevelEditor/PowerNetLevelTableSVG'
import { powerNetLevels } from 'i18n/displayConstants/featureOutputs'
import { StyledSelectMenu } from 'components/feature/NavigatorPanes/editors/base/InputValueEditor/InputValueEditor.style.js'

const { BATTERY_VOLTAGE, IGNITION_STATUS, ENGINE_SPEED } =
  POWER_NET_LEVEL_SIGNAL_URIS

export default connect(
  (state) => {
    const { batteryVoltage, engineSpeed, ignitionStatus } =
      getPowerNetLevelSimulationValues(state)
    return {
      batteryVoltage,
      engineSpeed,
      ignitionStatus,
    }
  },
  (dispatch) => ({
    updateSimulatedValue: (uri, value) => {
      dispatch(updateSimulatedValue(uri, value))
    },
  })
)((props) => {
  const {
    batteryVoltage,
    updateSimulatedValue,
    ignitionStatus,
    engineSpeed,
    powerNetLevel,
  } = props
  return (
    <>
      <SimulationWrapper>
        <BiggerHeader type="h4">
          {strings.power_net_and_voltage_conditions}{' '}
          <HoverableInfoIcon
            overlayClassName="autoWidth svg-tooltip"
            tooltip={<PowerNetLevelTable />}
            tooltipPosition="topRight"
          />
        </BiggerHeader>
        {powerNetLevel && (
          <Paragraph bold>
            {strings.power_net_simulated_level(
              `${powerNetLevel} - ${powerNetLevels(powerNetLevel)}`
            )}
          </Paragraph>
        )}
        <PowerComponentSimulator
          {...EngineSpeedProps}
          simulatedValue={engineSpeed}
          updateSimulatedValue={updateSimulatedValue}
        />

        <PowerComponentSimulator
          {...IgnitionStatusProps}
          simulatedValue={ignitionStatus}
          updateSimulatedValue={updateSimulatedValue}
        />

        <PowerComponentSimulator
          {...BatteryVoltageProps}
          simulatedValue={batteryVoltage}
          updateSimulatedValue={updateSimulatedValue}
        />
      </SimulationWrapper>
    </>
  )
})

const BatteryVoltageProps = {
  defaultValue: 0,
  uri: BATTERY_VOLTAGE,
  maxValue: 3212.8,
  selectedInputDescription: strings.battery_voltage_value,
  translationType: 'range_battery_plus',
  units: 'V',
  value: 0,
  translations: [{ translation: 'x*0.05' }],
}

const EngineSpeedProps = {
  defaultValue: 0,
  uri: ENGINE_SPEED,
  maxValue: 8031.9,
  operator: 2,
  selectedInputDescription: strings.engine_state,
  translationType: 'state',
  units: 'RPM',
  value: 0,
  translations: [
    {
      value: 0,
      translation: 'off',
    },
    {
      value: 1,
      translation: 'on',
    },
  ],
}

export const IgnitionStatusProps = {
  defaultValue: 0,
  uri: IGNITION_STATUS,
  maxValue: 3,
  selectedInputDescription: strings.ignition_status,
  translationType: 'state',
  value: 0,
  translations: [
    {
      value: 0,
      translation: 'off',
    },
    {
      value: 1,
      translation: 'accessory',
    },
    {
      value: 2,
      translation: 'run',
    },
    {
      value: 3,
      translation: 'crank',
    },
  ],
}

export const PowerComponentDefaultProps = {
  queryType: 'simple',
  showEvaluation: false,
  subType: 'nativeInput',
  dataType: 'nativeInput',
  rungType: 'nativeInput',
  canReset: true,
}

export const PowerComponentSimulator = ({
  updateSimulatedValue,
  defaultValue,
  uri,
  ...props
}) => {
  const [hasChanged, updateHasChanged] = React.useState(false)
  const handleUpdateSimulatedValue = (value) => {
    updateSimulatedValue(uri, value)
    updateHasChanged(true)
  }
  const handleResetSimulatedValue = () => {
    updateSimulatedValue(uri, defaultValue)
    updateHasChanged(false)
  }
  return (
    <ConditionSimulator
      {...PowerComponentDefaultProps}
      {...{ defaultValue, uri, ...props }}
      inputUri={uri}
      minValue={0}
      handleUpdateSimulatedValue={handleUpdateSimulatedValue}
      resetSimulatedValue={handleResetSimulatedValue}
      canReset={hasChanged}
    />
  )
}

PowerComponentSimulator.propTypes = {
  defaultValue: PropTypes.number,
  uri: PropTypes.string.isRequired,
  handleUpdateSimulatedValue: PropTypes.func.isRequired,
  updateSimulatedValue: PropTypes.func.isRequired,
}

const SimulationWrapper = styled.div`
  border-bottom: 1px solid ${themeColor('greyPrimary')};
  hr:last-of-type {
    display: none;
  }
  ${StyledSelectMenu} {
    width: 50%;
  }
`

const BiggerHeader = styled(Header)`
  font-size: 16px;
  margin-bottom: 8px;
  color: ${themeColor('blackSecondary')};
`
